import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useAuth } from '@renderer/chat/providers'
import type { AskUserQuestionAnswer } from '@renderer/chat/integrations/ask-user-question'
import { STAGED_ACTIONS_QUERY_KEY, type StagedAction } from './use-staged-actions'
import { useResumeChatAfterInvoke } from './staged-actions/resume-chat'
import { answerableResumeLabel } from '../views/chat-output-view/chat-output'

/**
 * Answers a pending background AskUserQuestion staged action via
 * `POST /v1/staged-actions/:id/answer`. The backend stores the answer, resolves
 * the action, and fires its `resume_chat` hook so the run that asked the
 * question continues with the answer in context.
 *
 * This is the *background* counterpart to the foreground inline flow (which
 * posts an MCP tool-result). Both surfaces render the same `AskUserQuestionCard`.
 */
export function useAnswerQuestionAction() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const resumeChatAfterAnswer = useResumeChatAfterInvoke()

  const mutation = useMutation({
    mutationFn: async ({ actionId, answer }: { actionId: string; answer: AskUserQuestionAnswer }) => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${actionId}/answer`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(answer),
      })
      if (!response.ok) {
        throw new Error(`Failed to answer question (status ${response.status})`)
      }
      // The answer is already persisted if the response body fails to parse.
      try {
        return ((await response.json()) as { data?: StagedAction }).data ?? null
      } catch {
        return null
      }
    },
    onSuccess: (resolvedAction, { actionId, answer }) => {
      // Reflect the answer in the cache immediately, before the refetch lands.
      // Two things depend on this:
      //   1. `getActionStatus` treats any non-null `outputData` as 'completed',
      //      so the answered question drops out of the pending panel (and the
      //      toast renders its done state) right away instead of still showing
      //      the primary "Answer" affordance until the refetch removes it.
      //   2. `QuestionActionContent` derives `existingResult` from `outputData`,
      //      so reopening the drawer before the refetch renders the card
      //      read-only — a second answer can't be posted for the same action.
      const now = new Date().toISOString()
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) =>
          old?.map((a) =>
            a.id === actionId
              ? { ...a, outputData: answer as unknown as StagedAction['outputData'], resolvedAt: a.resolvedAt ?? now }
              : a,
          ) ?? old,
      )

      // Show the answer summary until the backend's resumed turn arrives.
      resumeChatAfterAnswer(resolvedAction, {
        label: resolvedAction ? (answerableResumeLabel(resolvedAction) ?? undefined) : undefined,
      })
    },
    onSettled: () => {
      // Reconcile with the server (resolved actions leave the pending list).
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
    },
  })

  // Stable callback that throws on failure, matching `AskUserQuestionCard`'s
  // `onSubmit` contract (it keeps Submit visible for retry when onSubmit throws).
  const answerQuestion = useCallback(
    async (actionId: string, answer: AskUserQuestionAnswer): Promise<void> => {
      await mutation.mutateAsync({ actionId, answer })
    },
    [mutation],
  )

  return { answerQuestion, isAnswering: mutation.isPending }
}
